import {
  Flag,
  countryName,
  findTowns,
  type Point,
  type Town,
  type TownSearch,
} from '@trippath/shared';
import { useEffect, useId, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

/** How long typing must pause before towns are looked up: OpenStreetMap is asked sparingly. */
export const PAUSE = 350;

interface Props {
  label: string;
  city: string;
  /** The point picked, if one was. */
  place: Point | null;
  /** The stop is drawn by its city's airport, no point being picked. */
  guessed: boolean;
  error: string | undefined;
  inputRef: RefObject<HTMLInputElement | null>;
  /** The name typed; the point picked before no longer holds. */
  onType: (city: string) => void;
  onPick: (town: Town) => void;
}

/**
 * The city of a stop, typed, with the towns of that name to pick from: a pick sets the name,
 * the country and the point the map draws it at.
 */
export function TownField({
  label,
  city,
  place,
  guessed,
  error,
  inputRef,
  onType,
  onPick,
}: Props) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [typed, setTyped] = useState<string | null>(null);
  const [found, setFound] = useState<TownSearch | 'failed' | null>(null);

  useEffect(() => {
    if (typed === null || typed.trim().length < 2) return;
    const stop = new AbortController();
    const timer = setTimeout(() => {
      findTowns(typed, stop.signal).then(setFound, () => {
        setFound(stop.signal.aborted ? null : 'failed');
      });
    }, PAUSE);
    return () => {
      clearTimeout(timer);
      stop.abort();
    };
  }, [typed]);

  const searching = typed !== null && typed.trim().length >= 2;
  const towns = searching && found !== null && found !== 'failed' ? found.towns : [];
  let note = t('stopForm.offMap');
  if (place !== null) note = t('stopForm.onMap');
  else if (guessed) note = t('stopForm.byAirport');
  const unreached =
    searching && (found === 'failed' || (found !== null && found.osm === 'failed'));

  return (
    <div className="field town-field">
      <label className="field-label" htmlFor={id} data-required>
        {label}
      </label>
      <input
        ref={inputRef}
        id={id}
        className="text-input"
        type="text"
        autoComplete="off"
        value={city}
        aria-invalid={error !== undefined}
        aria-describedby={`${error === undefined ? '' : `${id}-error `}${id}-note`}
        onChange={(event) => {
          setTyped(event.target.value);
          onType(event.target.value);
        }}
      />
      {error !== undefined && (
        <p className="field-error" id={`${id}-error`}>
          {error}
        </p>
      )}
      <p className="quiet town-note" id={`${id}-note`}>
        {note}
      </p>
      {unreached && <p className="quiet">{t('stopForm.osmFailed')}</p>}
      {searching && found !== null && found !== 'failed' && towns.length === 0 && (
        <p className="quiet">{t('stopForm.noTowns')}</p>
      )}
      {towns.length > 0 && (
        <ul className="towns" aria-label={t('stopForm.towns')}>
          {towns.map((town) => (
            <li key={`${town.name}-${town.country}-${String(town.lat)}`}>
              <button
                type="button"
                className="option"
                onClick={() => {
                  setTyped(null);
                  setFound(null);
                  onPick(town);
                }}
              >
                <span>
                  <span className="option-city">{town.name}</span>
                  <span className="option-where">
                    <Flag country={town.country} />
                    {countryName(town.country, i18n.language)}
                    {town.detail === '' ? '' : ` · ${town.detail}`}
                    {town.airport ? ` · ${t('stopForm.airport')}` : ''}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
